import dayjs from "dayjs";

import { isLocal } from "./areas";
import { placeKey } from "./city-names";
import { COST_INDEX_BASE } from "./constants";
import { costIndexFor } from "./cost-index";
import { canPriceFares } from "./trip-dates";
import type { ProposalRates } from "./proposal-rates";
import type {
  AssumptionField,
  CityEstimate,
  Estimate,
  FareQuote,
  HotelRate,
  Origin,
  RateSource,
  RouteFare,
  UsdRange,
  Workspace,
} from "./types";
import { cityKey, originKey, routeKey, travelerCount, unknownTravelers } from "./workspace";

const ZERO: UsdRange = { low: 0, high: 0 };

/**
 * Multiplies both ends of a range, e.g. per person to group.
 * @param range - The range.
 * @param factor - What to multiply by.
 * @returns The scaled range.
 * @throws Never.
 */
export const scale = (range: UsdRange, factor: number): UsdRange => ({
  low: range.low * factor,
  high: range.high * factor,
});

/**
 * A city's flight cost in the current view: the average fare per person, or that fare times the
 * people who have one for the group. Null when no fare is known.
 * @param flights - The city's flights.
 * @param totalMode - Show the group's cost instead of one person's.
 * @returns The range (one value at both ends), or null.
 * @throws Never.
 */
export const flightsRange = (
  flights: Pick<CityEstimate["flights"], "averageUsd" | "pricedPeople">,
  totalMode: boolean
): UsdRange | null => {
  if (flights.averageUsd === null) return null;
  const fare = flights.averageUsd * (totalMode ? flights.pricedPeople : 1);
  return { low: fare, high: fare };
};

const add = (...ranges: UsdRange[]): UsdRange =>
  ranges.reduce((sum, range) => ({ low: sum.low + range.low, high: sum.high + range.high }), ZERO);

/**
 * Room nights between the trip dates.
 * @param dates - Trip dates, or null before they are set.
 * @returns Nights, 0 for a same-day trip or no dates.
 * @throws Never.
 */
export const nightsBetween = (dates: Workspace["dates"]): number => {
  if (!dates) return 0;
  const nights = dayjs(dates.end).diff(dayjs(dates.start), "day");
  return Number.isFinite(nights) && nights > 0 ? nights : 0;
};

/**
 * Average fare per person to one city, weighted by how many fly in each traveler group.
 *
 * A group with no fare is left out of the average and listed in `missing`, never counted as a
 * $0 fare. A fare the planner typed in replaces the live one.
 *
 * @param origins - Traveler groups.
 * @param destination - Destination city.
 * @param workspace - Supplies dates and fare overrides.
 * @param fares - Per-traveler fares for the current search, keyed by `routeKey(originKey, destination)`.
 * @returns The weighted average (null when nothing priced), the gaps, whether fares are pending, and every route.
 * @throws Never.
 */
export const weightedFlightAverage = (
  origins: Origin[],
  destination: string,
  workspace: Pick<Workspace, "dates" | "fareOverrides">,
  fares: ReadonlyMap<string, FareQuote>
): CityEstimate["flights"] => {
  const routes = origins.map((origin) => routeFare(origin, destination, workspace, fares));
  const flyers = routes.filter((route) => route.status !== "local");
  const priced = routes.filter((route) => route.usd !== null);
  const pricedPeople = priced.reduce((sum, route) => sum + route.count, 0);
  const weightedSum = priced.reduce((sum, route) => sum + (route.usd ?? 0) * route.count, 0);
  // Locals alone are not a fare: with every flyer's fare missing, the average is unknown, not $0.
  const known = flyers.length > 0 ? flyers.some((route) => route.usd !== null) : pricedPeople > 0;
  return {
    averageUsd: workspace.dates && known ? weightedSum / pricedPeople : null,
    // Locals count as priced ($0 flight) even when every flyer's fare is missing.
    pricedPeople: workspace.dates ? pricedPeople : 0,
    missing: routes
      .filter((route) => route.status === "missing" || route.status === "unchecked")
      .map((route) => ({ city: route.group ? `${route.group} (${route.origin})` : route.origin, count: route.count })),
    loading: routes.some((route) => route.status === "loading"),
    routes,
  };
};

/** One group's fare to the destination: the planner's override, a live fare, or why there is none. */
const routeFare = (
  origin: Origin,
  destination: string,
  workspace: Pick<Workspace, "dates" | "fareOverrides">,
  fares: ReadonlyMap<string, FareQuote>
): RouteFare => {
  const key = originKey(origin);
  const base = {
    key,
    origin: origin.city,
    group: origin.group ?? null,
    count: origin.count,
    airports: null,
    flight: null,
    reason: null,
  };
  // People from the destination's city or metro drive in: no flight, $0.
  if (isLocal(origin.city, destination)) return { ...base, status: "local", usd: 0 };
  const override = workspace.fareOverrides[routeKey(key, destination)];
  const quote = workspace.dates ? fares.get(routeKey(key, destination)) : undefined;
  const airports =
    quote?.status === "priced" ? { origin: quote.originAirport, destination: quote.destinationAirport } : null;
  const flight = quote?.status === "priced" ? quote.flight : null;
  if (override !== undefined) return { ...base, status: "override", usd: override, airports, flight };
  if (!workspace.dates || !canPriceFares(workspace.dates)) return { ...base, status: "needsDates", usd: null };
  if (quote?.status === "unchecked") return { ...base, status: "unchecked", usd: null, reason: "Not checked" };
  if (!quote || quote.status === "loading") return { ...base, status: "loading", usd: null };
  if (quote.status === "priced") return { ...base, status: "priced", usd: quote.usd, airports, flight };
  return { ...base, status: "missing", usd: null, reason: quote.reason };
};

/**
 * Builds the full comparison from the workspace and whatever fares and hotel rates have landed.
 * The default meeting and meal rates are scaled by each city's cost index; a rate the planner
 * edited applies flat.
 * @param workspace - The workspace.
 * @param fares - Per-traveler fares keyed by `routeKey(originKey, destination)`.
 * @param hotels - Hotel rates keyed by {@link cityKey}.
 * @param proposals - The linked event's proposal rates keyed by {@link placeKey}; they replace venue
 *   rates and the daily meeting and meal rates for their city.
 * @returns Per-city estimates (USD), nights, headcount and the lowest city.
 * @throws Never.
 */
export const buildEstimate = (
  workspace: Workspace,
  fares: ReadonlyMap<string, FareQuote>,
  hotels: ReadonlyMap<string, HotelRate>,
  proposals: ReadonlyMap<string, ProposalRates> = new Map()
): Estimate => {
  const nights = nightsBetween(workspace.dates);
  const days = Math.max(nights, 1);
  const headcount = travelerCount(workspace);

  const cities = workspace.destinations.map((city): CityEstimate => {
    const flights = weightedFlightAverage(workspace.origins, city, workspace, fares);
    const rate = hotels.get(cityKey(city)) ?? { status: "loading" };
    const quoted = proposals.get(placeKey(city));
    // A city with no venue rate data leaves the hotel out and says so, like a missing fare.
    const hotel: CityEstimate["hotel"] = quoted?.roomPerNight
      ? {
          total: scale(quoted.roomPerNight, nights),
          source: "proposals",
          venueCount: quoted.count,
          loading: false,
          failed: false,
        }
      : {
          total: rate.status === "venues" ? scale(rate.perNight, nights) : { low: 0, high: 0 },
          source: rate.status === "venues" ? "venues" : "none",
          venueCount: rate.status === "venues" ? rate.venueCount : 0,
          loading: rate.status === "loading",
          failed: rate.status === "error",
          ...(rate.status === "venues"
            ? { perNight: rate.perNight, samples: rate.samples ?? [], updatedAt: rate.updatedAt ?? null }
            : {}),
        };
    // Proposal totals are for the whole group, so they split per person.
    const perHead = (total: UsdRange | null | undefined) =>
      total && headcount > 0 ? scale(total, 1 / headcount) : null;
    const quotedMeeting = perHead(quoted?.meetingTotal);
    const quotedMeals = perHead(quoted?.foodTotal);
    const costIndex = costIndexFor(city, workspace.places?.[cityKey(city)]);
    // A default rate follows the city's prices; one the planner set stays flat everywhere.
    const daily = (field: AssumptionField, quotedTotal: UsdRange | null): { range: UsdRange; source: RateSource } => {
      if (quotedTotal) return { range: quotedTotal, source: "proposals" };
      const rate = workspace.assumptions[field];
      if (!costIndex || workspace.assumptions.edited?.includes(field)) {
        return { range: scale(rate, days), source: "estimate" };
      }
      return { range: scale(rate, (days * costIndex.index) / COST_INDEX_BASE), source: "adjusted" };
    };
    const meeting = daily("meetingSpacePerDay", quotedMeeting);
    const food = daily("mealsPerDay", quotedMeals);
    const meetingSpace = meeting.range;
    const meals = food.range;
    const sources: CityEstimate["sources"] = { meeting: meeting.source, meals: food.source };
    const flightCost = flights.averageUsd ?? 0;
    const stay = add(hotel.total, meetingSpace, meals);
    const perPerson = add({ low: flightCost, high: flightCost }, stay);
    // Only fares that are known count: people without a home city or a fare add their stay, not a guessed flight.
    const groupFlights = flightCost * flights.pricedPeople;
    const total = add({ low: groupFlights, high: groupFlights }, scale(stay, headcount));
    return { city, flights, hotel, meetingSpace, meals, sources, costIndex, perPerson, total };
  });

  // "Lowest" only means something once real fares exist for real people on real dates.
  const priced = workspace.dates !== null && workspace.origins.length > 0 && headcount > 0;
  return {
    nights,
    days,
    headcount,
    withoutHomeCity: unknownTravelers(workspace),
    cities,
    lowestCity: lowestCity(cities, priced),
  };
};

/**
 * The cheapest city, or null until there are dates, home cities and at least two cities with
 * priced flights. A city still loading or with no priced flights cannot win.
 */
const lowestCity = (cities: CityEstimate[], priced: boolean): string | null => {
  if (!priced || cities.length < 2) return null;
  if (cities.some((city) => city.flights.loading || city.hotel.loading)) return null;
  // A city missing its hotel would look cheaper than it is, so it can't be the lowest.
  const comparable = cities.filter((city) => city.flights.averageUsd !== null && city.hotel.source !== "none");
  if (comparable.length < 2) return null;
  return comparable.reduce((best, city) => (city.perPerson.low < best.perPerson.low ? city : best)).city;
};

/**
 * Converts USD to the display currency with a USD-based rate map.
 * @param usd - Amount in USD.
 * @param currency - Target ISO code.
 * @param rates - USD value of one unit of each currency, as `getDisplayFxRates` returns.
 * @returns The converted amount, or null when the rate is missing.
 * @throws Never.
 */
export const fromUsd = (usd: number, currency: string, rates: Readonly<Record<string, number>>): number | null => {
  if (currency === "USD") return usd;
  const usdPerUnit = rates[currency];
  if (!usdPerUnit) return null;
  return usd / usdPerUnit;
};

/**
 * Converts an amount in the display currency back to USD, for edits the user types.
 * @param amount - Amount in `currency`.
 * @param currency - Its ISO code.
 * @param rates - USD value of one unit of each currency.
 * @returns USD, or null when the rate is missing.
 * @throws Never.
 */
export const toUsd = (amount: number, currency: string, rates: Readonly<Record<string, number>>): number | null => {
  if (currency === "USD") return amount;
  const usdPerUnit = rates[currency];
  if (!usdPerUnit) return null;
  return amount * usdPerUnit;
};

/** An input the estimate needs before it can price something. */
export type MissingInput = "dates" | "origins" | "destinations";

/**
 * Inputs the estimate is missing, most blocking first. Dates gate fares and hotels; home cities gate fares.
 * @param workspace - The workspace.
 * @returns The missing inputs, empty when everything can be priced.
 * @throws Never.
 */
export const missingInputs = (workspace: Workspace): MissingInput[] => {
  const missing: MissingInput[] = [];
  if (!workspace.dates) missing.push("dates");
  if (workspace.origins.length === 0) missing.push("origins");
  if (workspace.destinations.length === 0) missing.push("destinations");
  return missing;
};
